import React from 'react';
import { Crown } from 'lucide-react';
import { Organization } from '../../../types/domain';
import { Modal } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { Badge } from '../../../components/ui/Badge';

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  organizations: Organization[];
  selectedOrgId: string;
  onSelectOrgId: (id: string) => void;
  selectedTier: 'free' | 'pro' | 'enterprise';
  onSelectTier: (tier: 'free' | 'pro' | 'enterprise') => void;
  isUpgrading: boolean;
  currentSelectedOrg?: Organization;
  onConfirmUpgrade: () => void;
}

export const ChangePlanModal: React.FC<ChangePlanModalProps> = ({
  isOpen,
  onClose,
  organizations,
  selectedOrgId,
  onSelectOrgId,
  selectedTier,
  onSelectTier,
  isUpgrading,
  currentSelectedOrg,
  onConfirmUpgrade,
}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Change Plan for ${currentSelectedOrg?.name || 'Organization'}`}
    >
      <div className="space-y-4">
        {organizations.length > 1 && (
          <Select
            label="Target Organization:"
            size="xs"
            value={selectedOrgId}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => onSelectOrgId(e.target.value)}
            options={organizations.map((org) => ({
              value: org.id,
              label: `${org.name} (Current: ${org.plan_tier?.toUpperCase() || 'FREE'})`,
            }))}
          />
        )}

        <div className="space-y-2.5">
          {/* Free Plan */}
          <div
            onClick={() => onSelectTier('free')}
            className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
              selectedTier === 'free'
                ? 'bg-cf-surface border-cf-orange shadow-xs'
                : 'bg-cf-bg border-cf-border hover:border-cf-borderHover'
            }`}
          >
            <div className="flex items-center justify-between">
              <div>
                <span className="text-xs font-bold text-cf-textPrimary block">Free Plan</span>
                <span className="text-xs text-cf-textSecondary">
                  Perfect for personal hobby projects
                </span>
              </div>
              <span className="text-xs font-sans font-bold tabular-nums text-cf-textPrimary">
                $0 / mo
              </span>
            </div>
            <div className="mt-2 text-xs text-cf-textMuted flex items-center gap-3">
              <span>5,000 submissions/mo</span>
              <span>•</span>
              <span>3 projects</span>
              <span>•</span>
              <span>5 tables</span>
            </div>
          </div>

          {/* Pro Plan */}
          <div
            onClick={() => onSelectTier('pro')}
            className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
              selectedTier === 'pro'
                ? 'bg-cf-surface border-cf-orange shadow-xs'
                : 'bg-cf-bg border-cf-border hover:border-cf-borderHover'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-xs font-bold text-cf-textPrimary">Pro Plan</span>
                <Badge variant="orange" size="xs">
                  Recommended
                </Badge>
              </div>
              <span className="text-xs font-sans font-bold tabular-nums text-cf-textPrimary">
                $29 / mo
              </span>
            </div>
            <div className="mt-2 text-xs text-cf-textMuted flex items-center gap-3">
              <span>100,000 submissions/mo</span>
              <span>•</span>
              <span>Unlimited projects</span>
              <span>•</span>
              <span>Email notifications</span>
            </div>
          </div>

          {/* Enterprise Plan */}
          <div
            onClick={() => onSelectTier('enterprise')}
            className={`p-3.5 rounded-lg border cursor-pointer transition-all ${
              selectedTier === 'enterprise'
                ? 'bg-cf-surface border-cf-orange shadow-xs'
                : 'bg-cf-bg border-cf-border hover:border-cf-borderHover'
            }`}
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold text-cf-textPrimary">Enterprise Plan</span>
              <span className="text-xs font-sans font-bold tabular-nums text-cf-textPrimary">
                $199 / mo
              </span>
            </div>
            <div className="mt-2 text-xs text-cf-textMuted flex items-center gap-3">
              <span>1,000,000+ submissions/mo</span>
              <span>•</span>
              <span>Custom webhooks</span>
              <span>•</span>
              <span>Dedicated support</span>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2 border-t border-cf-border">
          <Button variant="secondary" size="xs" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="xs"
            disabled={isUpgrading}
            isLoading={isUpgrading}
            leftIcon={<Crown size={13} />}
            onClick={onConfirmUpgrade}
          >
            Confirm {selectedTier.toUpperCase()} Plan
          </Button>
        </div>
      </div>
    </Modal>
  );
};
